import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAccounts } from "@/lib/accounts";
import { byReliability } from "@/lib/aggregator";
import { accountOptionLabel, canAct } from "@/lib/ownChats";
import { SimpleSelect } from "@/components/ui/select";

/**
 * Which account acts: those that can right now, the most reliable first — and
 * that one chosen until somebody chooses another.
 */
export function AccountSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const accounts = useAccounts();
  const usable = useMemo(
    () => (accounts.data ?? []).filter(canAct).sort(byReliability),
    [accounts.data],
  );
  const offered = usable[0]?.id;
  const chosen = usable.some((a) => a.id === value);

  useEffect(() => {
    if (!chosen && offered) onChange(offered);
  }, [chosen, offered, onChange]);

  return (
    <>
      <SimpleSelect
        value={chosen ? value : ""}
        onValueChange={onChange}
        disabled={disabled || accounts.isLoading}
        placeholder={t("aggregator.bots.account_placeholder") ?? undefined}
        options={usable.map((a) => ({
          value: a.id,
          label:
            accountOptionLabel(a, t("own_chats.account_not_warmed")) +
            (a.spam_state === "limited" ? ` · ${t("aggregator.bots.account_limited")}` : ""),
        }))}
      />
      {accounts.data && usable.length === 0 && (
        <p className="text-xs text-danger">{t("aggregator.bots.account_none")}</p>
      )}
    </>
  );
}
